import { useCallback, useEffect, useRef } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components/src/ui/tabs';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { Monitor, Smartphone, ZoomIn, ZoomOut } from 'lucide-react';

import {
  previewDisplayDeviceAtom,
  previewModeAtom,
} from '@/modules/annotator/multiFileViewer/imageToolbar/atoms';
import type { GetClaimSummaryResponse } from '@/modules/insuranceExport/types';

interface ZoomToolTopbarProps {
  contentRef: React.RefObject<HTMLDivElement>;
  componentRef: React.RefObject<HTMLDivElement>;
  scale: number;
  selectedTreatmentIds?: string[];
  claimSummary?: GetClaimSummaryResponse;
  hasInitiallyScaled: boolean;
  isDarkMode?: boolean;
  onSetHasInitiallyScaled: React.Dispatch<React.SetStateAction<boolean>>;
  onSetScale: React.Dispatch<React.SetStateAction<number>>;
}

export const ZoomToolTopbar = ({
  contentRef,
  componentRef,
  scale,
  selectedTreatmentIds,
  claimSummary,
  hasInitiallyScaled,
  isDarkMode = false,
  onSetHasInitiallyScaled,
  onSetScale,
}: ZoomToolTopbarProps) => {
  const { t } = useLingui();
  const prevHasQuadTreatmentsRef = useRef(false);
  const previewMode = useAtomValue(previewModeAtom);
  const [previewDisplayDevice, setPreviewDisplayDevice] = useAtom(
    previewDisplayDeviceAtom,
  );

  const getTriggerClassName = (triggerMode: 'mobile' | 'desktop') => {
    const isSelected = previewDisplayDevice === triggerMode;

    return classNames('vai-h-full', {
      '!vai-bg-background-muted': isDarkMode && isSelected,
      'hover:vai-bg-background-muted': isDarkMode && !isSelected,
      'vai-bg-gray-800': !isDarkMode && isSelected,
      'hover:vai-bg-zinc-200': !isDarkMode && !isSelected,
    });
  };

  const updateScale = useCallback(() => {
    if (!contentRef.current || !componentRef.current) return;

    const container = contentRef.current.parentElement;
    if (!container) return;

    const containerPadding =
      parseInt(window.getComputedStyle(container).paddingLeft) +
      parseInt(window.getComputedStyle(container).paddingRight);
    const availableWidth = container.clientWidth - containerPadding;

    const contentElement = componentRef.current;

    const originalTransform = contentElement.style.transform;
    contentElement.style.transform = 'none';
    const trueContentWidth = contentElement.scrollWidth;
    contentElement.style.transform = originalTransform;

    const newScale = Math.min(1, availableWidth / trueContentWidth);

    onSetScale(newScale);
  }, [contentRef, componentRef, onSetScale]);

  const handleZoom = useCallback(
    (direction: 'in' | 'out') => {
      // Get the scrollable container that wraps the zoomed content (2 levels up from the scaled content)
      const container = componentRef.current?.parentElement?.parentElement;
      if (!container) return;

      const oldScale = scale;
      const newScale =
        direction === 'in'
          ? Math.min(scale + 0.1, 1.5)
          : Math.max(scale - 0.1, 0.5);

      // Get current scroll position and container dimensions
      const scrollLeft = container.scrollLeft;
      const scrollTop = container.scrollTop;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      // Calculate the center point of the viewport
      const viewportCenterX = scrollLeft + containerWidth / 2;
      const viewportCenterY = scrollTop + containerHeight / 2;

      // Calculate the point in the content that's at the center of the viewport
      const contentCenterX = viewportCenterX / oldScale;
      const contentCenterY = viewportCenterY / oldScale;

      onSetScale(newScale);

      // Wait for the next animation frame to ensure the scale change has been applied
      // before updating the scroll position. This prevents jittery scrolling during zoom.
      requestAnimationFrame(() => {
        container.scrollLeft = contentCenterX * newScale - containerWidth / 2;
        container.scrollTop = contentCenterY * newScale - containerHeight / 2;
      });
    },
    [scale, componentRef, onSetScale],
  );

  const handleScaleChange = useCallback(
    (value: string) => {
      if (value === 'fit') {
        updateScale();
      } else {
        const newScale = parseFloat(value);
        onSetScale(newScale);
      }
    },
    [updateScale, onSetScale],
  );

  // Add transition style to the content element
  useEffect(() => {
    if (componentRef.current) {
      componentRef.current.style.transition = 'transform 0.2s ease-out';
    }
  }, [componentRef]);

  // Initial scale when data loads
  // Fit to page after selected treatments are loaded
  useEffect(() => {
    if (!hasInitiallyScaled && selectedTreatmentIds?.length) {
      updateScale();
      onSetHasInitiallyScaled(true);
    }
  }, [
    hasInitiallyScaled,
    updateScale,
    onSetHasInitiallyScaled,
    selectedTreatmentIds?.length,
  ]);

  // Watch for changes in quad treatments since the FMX table is wider
  useEffect(() => {
    if (!selectedTreatmentIds?.length || !claimSummary?.treatments) return;

    // Check if we have any quad treatments in the current selection
    const hasQuadTreatments = selectedTreatmentIds.some((id) => {
      const treatment = claimSummary.treatments.find(
        (tx) => tx.txId.toString() === id,
      );
      return treatment?.quadrant;
    });

    // Only adjust scale if we're switching between having and not having quad treatments
    if (hasQuadTreatments !== prevHasQuadTreatmentsRef.current) {
      // Reset scale to 1 first to ensure accurate measurements
      onSetScale(1);
      // Use a timeout to allow the DOM to update before recalculating scale
      setTimeout(updateScale, 0);
      // Update the ref with current state
      prevHasQuadTreatmentsRef.current = hasQuadTreatments;
    }
  }, [selectedTreatmentIds, claimSummary, updateScale, onSetScale]);

  // Handle window resize
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateScale, 100); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [updateScale]);

  return (
    <div className="vai-flex vai-gap-2 vai-py-2 vai-items-center vai-justify-start">
      <div className="vai-flex vai-gap-2">
        <Button
          variant="outline"
          size="icon"
          title={t`Zoom Out`}
          onClick={() => handleZoom('out')}
          className={classNames(
            'vai-rounded-md vai-aspect-square hover:vai-bg-gray-200',
            {
              'vai-border-sidebar-border vai-bg-transparent hover:vai-bg-background-muted':
                isDarkMode,
            },
          )}
        >
          <ZoomOut
            className={classNames('vai-h-5 vai-w-5', {
              'vai-text-foreground-dark': isDarkMode,
            })}
          />
        </Button>
        <Select value={scale.toString()} onValueChange={handleScaleChange}>
          <SelectTrigger
            className={classNames('vai-min-w-20 hover:vai-bg-gray-200', {
              'vai-border-sidebar-border vai-bg-transparent vai-text-foreground-dark hover:vai-bg-background-muted':
                isDarkMode,
            })}
          >
            <SelectValue>{Math.round(scale * 100)}%</SelectValue>
          </SelectTrigger>
          <SelectContent
            className={classNames(
              'vai-z-[9999]',
              {
                'vai-bg-background-dark vai-text-foreground-dark vai-border-sidebar-border':
                  isDarkMode,
              },
              {
                '[&>*]:vai-text-foreground-dark [&>*]:vai-bg-background-muted ':
                  isDarkMode,
              },
            )}
          >
            <SelectItem
              className={classNames({
                'focus:vai-bg-background-gray focus:vai-text-foreground-dark':
                  isDarkMode,
              })}
              value="fit"
            >
              <Trans>Fit to Page</Trans>
            </SelectItem>
            <SelectItem
              className={classNames({
                'focus:vai-bg-background-gray focus:vai-text-foreground-dark':
                  isDarkMode,
              })}
              value="0.5"
            >
              50%
            </SelectItem>
            <SelectItem
              className={classNames({
                'focus:vai-bg-background-gray focus:vai-text-foreground-dark':
                  isDarkMode,
              })}
              value="1"
            >
              100%
            </SelectItem>
            <SelectItem
              className={classNames({
                'focus:vai-bg-background-gray focus:vai-text-foreground-dark':
                  isDarkMode,
              })}
              value="1.25"
            >
              125%
            </SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          title={t`Zoom In`}
          onClick={() => handleZoom('in')}
          className={classNames(
            'vai-rounded-md vai-aspect-square hover:vai-bg-gray-200',
            {
              'vai-border-sidebar-border vai-bg-transparent hover:vai-bg-background-muted':
                isDarkMode,
            },
          )}
        >
          <ZoomIn
            className={classNames('vai-h-5 vai-w-5', {
              'vai-text-foreground-dark': isDarkMode,
            })}
          />
        </Button>
      </div>

      {previewMode === 'qrCode' && (
        <Tabs
          value={previewDisplayDevice}
          onValueChange={(value) =>
            setPreviewDisplayDevice(value as 'mobile' | 'desktop')
          }
          className="vai-ml-4"
        >
          <TabsList
            className={classNames('vai-h-10 vai-p-1 vai-bg-transparent', {
              'vai-border vai-border-sidebar-border': isDarkMode,
            })}
          >
            <TabsTrigger
              value="mobile"
              className={getTriggerClassName('mobile')}
            >
              <Smartphone
                className={classNames('vai-h-5 vai-w-5', {
                  'vai-text-foreground-dark': isDarkMode,
                })}
              />
            </TabsTrigger>
            <TabsTrigger
              value="desktop"
              className={getTriggerClassName('desktop')}
            >
              <Monitor
                className={classNames('vai-h-5 vai-w-5', {
                  'vai-text-foreground-dark': isDarkMode,
                })}
              />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}
    </div>
  );
};
